import React from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { ExternalLink, Copy, Compass, Sparkles, ArrowLeftRight, ShieldCheck, Share2, Link2 } from 'lucide-react';
import { tokenUrl, txUrl, networkOf, uniswapUrl, verifyUrl } from '../lib/networks';
import { copyToClipboard } from '../lib/clipboard';
import { xShareUrl, tokenPageUrl } from '../lib/share';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const SuccessModal = ({ open, onClose, token }) => {
  if (!token) return null;
  const net = networkOf(token.chain_id);
  const uni = uniswapUrl(token.chain_id, token.contract_address);
  const verify = verifyUrl(token.chain_id, token.contract_address);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="success-modal" className="bg-[#0B0A2A]/95 backdrop-blur-2xl border border-[#7DF9FF]/40 rounded-3xl text-white max-w-lg cf-glow-cyan">
        <div className="flex items-center gap-2">
          <span className="cf-chip"><Sparkles className="w-3 h-3" /> Live on {net.name}</span>
        </div>
        <DialogTitle className="font-display text-3xl font-bold mt-3">
          <span className="cf-gradient-text">{token.name}</span> is deployed
        </DialogTitle>
        <p className="text-sm text-forge-muted">
          You just minted {Number(token.total_supply).toLocaleString()} <span className="text-white font-medium">${token.symbol}</span>.
          20% ({Number((BigInt(token.total_supply) * 20n) / 100n).toLocaleString()} tokens) is in your wallet — the other 80% is locked in a burned Uniswap V3 LP forever.
        </p>
        <div className="mt-5 space-y-2 text-sm">
          <Row label="Contract" value={token.contract_address} testid="success-contract" />
          <Row label="Creator" value={token.creator_address} testid="success-creator" />
          {token.tx_hash && <Row label="Tx hash" value={token.tx_hash} testid="success-tx" />}
          <Row label="Tax" value="1.5% forever (1.0% platform · 0.5% you)" testid="success-tax" copyable={false} />
        </div>

        {/* Primary actions */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          {uni ? (
            <a
              data-testid="success-trade-uniswap"
              href={uni}
              target="_blank"
              rel="noopener noreferrer"
              className="cf-btn-primary flex-1">
              <ArrowLeftRight className="w-4 h-4" /> Trade on Uniswap
            </a>
          ) : (
            <a
              data-testid="success-view-explorer"
              href={tokenUrl(token.chain_id, token.contract_address) || txUrl(token.chain_id, token.tx_hash)}
              target="_blank"
              rel="noopener noreferrer"
              className="cf-btn-primary flex-1">
              <ExternalLink className="w-4 h-4" /> View on Explorer
            </a>
          )}
          <Link
            data-testid="success-go-explore"
            to="/explore"
            onClick={onClose}
            className="cf-btn-secondary flex-1">
            <Compass className="w-4 h-4" /> See in feed
          </Link>
        </div>

        {/* Share — free marketing for the creator */}
        <div className="mt-3 flex gap-2">
          <a
            data-testid="success-share-x"
            href={xShareUrl(token)}
            target="_blank"
            rel="noopener noreferrer"
            className="cf-btn-secondary text-sm flex-1 justify-center !py-2.5">
            <Share2 className="w-4 h-4" /> Share on X
          </a>
          <button
            data-testid="success-copy-link"
            onClick={() => copyToClipboard(tokenPageUrl(token.contract_address, token.chain_id), 'Token link')}
            className="cf-btn-secondary text-sm flex-1 justify-center !py-2.5">
            <Link2 className="w-4 h-4" /> Copy link
          </button>
        </div>

        {/* Secondary actions */}
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <a
            data-testid="success-view-explorer-secondary"
            href={tokenUrl(token.chain_id, token.contract_address) || txUrl(token.chain_id, token.tx_hash)}
            target="_blank"
            rel="noopener noreferrer"
            className="cf-btn-ghost text-xs flex-1 justify-center">
            <ExternalLink className="w-3.5 h-3.5" /> Explorer
          </a>
          {verify && (
            <a
              data-testid="success-verify-basescan"
              href={verify}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => toast.info('Click "Verify & Publish" on the explorer page, then paste the source from CoinForgeToken.sol.')}
              className="cf-btn-ghost text-xs flex-1 justify-center">
              <ShieldCheck className="w-3.5 h-3.5" /> Verify source
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, testid, copyable = true }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/5">
    <span className="text-xs text-forge-muted w-20 shrink-0 uppercase tracking-wider font-display font-medium">{label}</span>
    <span data-testid={testid} className="break-all flex-1 font-code text-xs">{value}</span>
    {copyable && (
      <button
        onClick={() => copyToClipboard(value, label)}
        className="text-forge-muted hover:text-[#7DF9FF] transition-colors"
        aria-label="copy"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

export default SuccessModal;
